//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { useQuery } from '@tanstack/react-query';

import {
  getAdobeOAuthSettings,
  getAdobeWebServiceSettings,
  getCuralatePixelSettings,
  getGA4Settings,
  getGASettings,
} from '@apis/analytics-settings/analyticsSettingsApi';
import {
  IGetAdobeSettingsResponse,
  IGetCuralatePixelSettingsResponse,
  IGetGA4SettingsResponse,
  IGetGASettingsResponse,
} from '@apis/analytics-settings/analyticsSettingsApiModel';
import {
  getConnectedInstagramAccount,
  getConnectedInstagramAccountsMessagesAccessStatus,
} from '@apis/instagram-settings/gatewayInstagramSettingsApi';
import {
  GetConnectedInstagramAccountsMessagesAccessStatusResponse,
  IGetConnectedInstagramAccountResponse,
} from '@apis/instagram-settings/gatewayInstagramSettingsApiModel';
import { getOverviewItems } from '@apis/product-feed-health/productFeedHealthApi';
import { IProductFeedHealthOverviewResponse } from '@apis/product-feed-health/productFeedHealthApiModels';
import { getUserSettings } from '@apis/user-settings/userSettingsApi';
import IUserSettings from '@models/IUserSettings';
import { QueryKeyConstants } from '@queries/QueryKeyConstants';
import { FIVE_MINUTES_STALE_TIME } from '@queries/QueryOptionsConstants';

export const useConnectedInstagramAccountQuery = <
  TData = IGetConnectedInstagramAccountResponse,
>(
  select?: (data: IGetConnectedInstagramAccountResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialInstagramAccount,
    ],
    queryFn: async ({ signal }) => getConnectedInstagramAccount({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useCurrentUserSettingsQuery = <TData = IUserSettings>(
  select?: (data: IUserSettings) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsCurrentUser,
    ],
    queryFn: async ({ signal }) => getUserSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useProductFeedHealthOverviewQuery = <
  TData = IProductFeedHealthOverviewResponse,
>(
  select?: (data: IProductFeedHealthOverviewResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsProductFeedHealthOverview,
    ],
    queryFn: async ({ signal }) => getOverviewItems({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGoogleAnalyticsSettingsQuery = <TData = IGetGASettingsResponse>(
  select?: (data: IGetGASettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleAnalytics,
    ],
    queryFn: async ({ signal }) => getGASettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useGoogleAnalytics4SettingsQuery = <
  TData = IGetGA4SettingsResponse,
>(
  select?: (data: IGetGA4SettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsGoogle,
      QueryKeyConstants.AccountSettingsAnalyticsGoogleAnalytics4,
    ],
    queryFn: async ({ signal }) => getGA4Settings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useAdobeWebServiceSettingsQuery = <
  TData = IGetAdobeSettingsResponse,
>(
  select?: (data: IGetAdobeSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsAdobe,
      QueryKeyConstants.AccountSettingsAnalyticsAdobeWebService,
    ],
    queryFn: async ({ signal }) => getAdobeWebServiceSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useAdobeOAuthSettingsQuery = <TData = IGetAdobeSettingsResponse>(
  select?: (data: IGetAdobeSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsAdobe,
      QueryKeyConstants.AccountSettingsAnalyticsAdobeOAuth,
    ],
    queryFn: async ({ signal }) => getAdobeOAuthSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useVibePixelSettingsQuery = <
  TData = IGetCuralatePixelSettingsResponse,
>(
  select?: (data: IGetCuralatePixelSettingsResponse) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsAnalytics,
      QueryKeyConstants.AccountSettingsAnalyticsVibePixel,
    ],
    queryFn: async ({ signal }) => getCuralatePixelSettings({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};

export const useConnectedInstagramAccountsMessagesAccessStatusQuery = <
  TData = GetConnectedInstagramAccountsMessagesAccessStatusResponse,
>(
  select?: (
    data: GetConnectedInstagramAccountsMessagesAccessStatusResponse,
  ) => TData,
) => {
  return useQuery({
    queryKey: [
      QueryKeyConstants.AccountSettings,
      QueryKeyConstants.AccountSettingsSocial,
      QueryKeyConstants.AccountSettingsSocialInstagramAccount,
      QueryKeyConstants.AccountSettingsSocialInstagramAccountMessagesAccessStatus,
    ],
    queryFn: async ({ signal }) =>
      getConnectedInstagramAccountsMessagesAccessStatus({ signal }),
    select,
    staleTime: FIVE_MINUTES_STALE_TIME,
  });
};
